import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabase/supabaseClient';
import { getUserProfile, ensureUserProfile } from '../lib/userProfileClient';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [authStatus, setAuthStatus] = useState('loading'); // 'loading', 'authenticated', 'unauthenticated'
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);

  const handleAuthChange = useCallback(async (session) => {
    if (session?.user) {
      // Session exists, now we must fetch the profile.
      // The status remains 'loading' until the profile is fetched.
      try {
        await ensureUserProfile(session.user.id, session.user.email);
        const userProfile = await getUserProfile(session.user.id);
        setUser(session.user);
        setProfile(userProfile);
        setAuthStatus('authenticated'); // Only authenticated once profile is loaded
      } catch (error) {
        console.error('Failed to fetch profile, signing out:', error);
        await supabase.auth.signOut();
        setAuthStatus('unauthenticated');
      }
    } else {
      // No session, user is unauthenticated.
      setUser(null);
      setProfile(null);
      setAuthStatus('unauthenticated');
    }
  }, []);

  useEffect(() => {
    // Check initial session on app load
    supabase.auth.getSession().then(({ data: { session } }) => {
      handleAuthChange(session);
    });

    // Listen for subsequent auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      handleAuthChange(session);
    });

    return () => subscription.unsubscribe();
  }, [handleAuthChange]);

  const refreshUserProfile = useCallback(async () => {
    if (user) {
      setAuthStatus('loading'); // Set to loading while we refresh
      try {
        const userProfile = await getUserProfile(user.id);
        setProfile(userProfile);
        setAuthStatus('authenticated'); // Back to authenticated once done
      } catch (error) {
        console.error('Failed to refresh profile, signing out:', error);
        await supabase.auth.signOut();
        setAuthStatus('unauthenticated');
      }
    }
  }, [user]);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
    // The onAuthStateChange listener will handle clearing user/profile state.
  }, []);

  const signIn = useCallback(async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    return { data, error };
  }, []);

  const signUp = useCallback(async (email, password) => {
    const result = await supabase.auth.signUp({ email, password });
    return result;
  }, []);

  const value = {
    user,
    profile,
    authStatus,
    refreshUserProfile,
    signOut,
    signIn,
    signUp,
    plan: profile?.plan || 'free',
  };

  return <AuthContext.Provider value={value}>{!children ? null : children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
